import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Edit, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormDrawer } from "@/components/ui/form-drawer";
import { useToast } from "@/hooks/use-toast";
import { StatusBadge } from "@/components/StatusBadge";
import { DateCell } from "@/components/DateCell";

interface Lead {
  id: string;
  name: string;
  code: string | null;
  classification: string;
  status: string;
  creditLimit: string | null;
  creditHold: boolean;
  createdAt: string;
  updatedAt: string | null;
}

const leadFormSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  code: z.string().optional(),
  status: z.string(),
});

type LeadFormData = z.infer<typeof leadFormSchema>;

export default function LeadDetailPage() {
  const { sessionId } = useAuth();
  const { id } = useParams();
  const [, setLocation] = useLocation();
  const [isEditing, setIsEditing] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: lead, isLoading } = useQuery<Lead>({
    queryKey: ["/api/leads", id],
    queryFn: async () => {
      return api.get<Lead>(`/leads/${id}`, sessionId);
    },
    enabled: !!id,
  });

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadFormSchema),
    values: lead
      ? {
          name: lead.name,
          code: lead.code || "",
          status: lead.status,
        }
      : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: LeadFormData) => {
      return api.patch<Lead>(`/leads/${id}`, data, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leads", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({
        title: "Lead updated",
        description: "The lead has been updated successfully.",
      });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update lead",
        variant: "destructive",
      });
    },
  });

  const convertMutation = useMutation({
    mutationFn: async () => {
      return api.post<Lead>(`/leads/${id}/convert`, {}, sessionId);
    },
    onSuccess: (data) => {
      toast({
        title: "Lead converted",
        description: "The lead has been successfully converted to an account.",
      });
      // Redirect to the account page
      setLocation(`/accounts/${data.id}`);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to convert lead.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: LeadFormData) => {
    updateMutation.mutate(data);
  };

  const handleConvert = () => {
    setConvertDialogOpen(true);
  };

  const handleConfirmConvert = () => {
    convertMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="space-y-6">
        <PageHeader title="Lead Not Found" showBackButton />
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">The lead you're looking for doesn't exist.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={lead.name}
        description={`Lead ${lead.code || ""}`}
        showBackButton
        actions={
          <div className="flex gap-2">
            {!isEditing && (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button onClick={handleConvert}>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Convert to Account
                </Button>
              </>
            )}
          </div>
        }
      />

      <FormDrawer
        open={isEditing}
        onOpenChange={(open) => {
          setIsEditing(open);
          if (!open) form.reset();
        }}
        title="Edit Lead"
        description="Update the lead information below."
        formId="edit-lead"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="md"
        data-testid="drawer-edit-lead"
      >
        <Form {...form}>
          <form
            id="edit-lead"
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name *</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>

      <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Lead Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{lead.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Code</Label>
                <p className="font-medium">{lead.code || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <div className="mt-1">
                  <StatusBadge status={lead.status} type="account" />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Classification</Label>
                <p className="font-medium capitalize">{lead.classification}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Created</Label>
                <div className="mt-1">
                  <DateCell value={lead.createdAt} includeTime />
                </div>
              </div>
              {lead.updatedAt && (
                <div>
                  <Label className="text-muted-foreground">Last Updated</Label>
                  <div className="mt-1">
                    <DateCell value={lead.updatedAt} includeTime />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      {/* Convert Lead Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Lead to Account</DialogTitle>
            <DialogDescription>
              Convert "{lead.name}" from a lead to a customer account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will change the classification from "prospect" to "customer" and make
              this lead a full account in your system.
            </p>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setConvertDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmConvert}
                disabled={convertMutation.isPending}
              >
                {convertMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Convert to Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
